import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  HouseholdDetail,
  HouseholdInput,
  HouseholdListResponse,
  HouseholdPatch,
} from "@guestbook/shared";
import { apiFetch, type ApiClientError } from "@/shared/lib/api";

const HOUSEHOLDS_KEY = "households" as const;

export interface HouseholdsListParams {
  cursor?: string;
  limit?: number;
}

function buildHouseholdsQuery(params: HouseholdsListParams): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function useHouseholdsList(params: HouseholdsListParams = {}) {
  return useQuery<HouseholdListResponse, ApiClientError>({
    queryKey: [HOUSEHOLDS_KEY, "list", params],
    queryFn: () =>
      apiFetch<HouseholdListResponse>(`/households${buildHouseholdsQuery(params)}`),
  });
}

export function useHousehold(id: string | undefined) {
  return useQuery<HouseholdDetail, ApiClientError>({
    queryKey: [HOUSEHOLDS_KEY, "detail", id],
    queryFn: () => apiFetch<HouseholdDetail>(`/households/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateHousehold() {
  const qc = useQueryClient();
  return useMutation<HouseholdDetail, ApiClientError, HouseholdInput>({
    mutationFn: (body) =>
      apiFetch<HouseholdDetail>("/households", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (household) => {
      qc.setQueryData([HOUSEHOLDS_KEY, "detail", household.id], household);
      qc.invalidateQueries({ queryKey: [HOUSEHOLDS_KEY, "list"] });
    },
  });
}

export function useUpdateHousehold(id: string) {
  const qc = useQueryClient();
  return useMutation<HouseholdDetail, ApiClientError, HouseholdPatch>({
    mutationFn: (body) =>
      apiFetch<HouseholdDetail>(`/households/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (household) => {
      qc.setQueryData([HOUSEHOLDS_KEY, "detail", household.id], household);
      qc.invalidateQueries({ queryKey: [HOUSEHOLDS_KEY, "list"] });
      // Membership patches can re-assign contacts; refresh the contact cache.
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useDeleteHousehold() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/households/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: [HOUSEHOLDS_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [HOUSEHOLDS_KEY, "list"] });
      // FK ON DELETE SET NULL detaches member contacts — refresh.
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}
